import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowDownRightBold,
    PiArrowUpRightBold,
    PiCalendarBlankBold,
    PiCaretDownBold,
    PiCaretRightBold,
    PiChartBarBold,
    PiCurrencyDollarBold,
    PiDownloadBold,
    PiGearBold,
    PiHourglassBold,
    PiPackageBold,
    PiPencilSimpleBold,
    PiPlusBold,
    PiReceiptBold,
    PiShoppingCartBold,
    PiTrashBold,
    PiTrendUpBold,
    PiUsersBold,
    PiWarningBold,
} from 'react-icons/pi';
import { PageHeader, LoadingState, MetricCard, MetricGrid, Button, Input } from '@v2/design-system';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { MonthlyComboChart } from './CommissionExecutiveCharts';
import GluteleaderInfluencersSection from './GluteleaderInfluencersSection';
import type {
    GluteleaderBeneficiary,
    GluteleaderDashboard,
    GluteleaderProductSummary,
    GluteleaderRoyaltyProduct,
} from './types';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

const qty = (v: number) =>
    new Intl.NumberFormat('pt-BR', { maximumFractionDigits: v % 1 === 0 ? 0 : 1 }).format(v ?? 0);

const pctLabel = (v: number | null | undefined) => {
    if (v == null) return '—';
    const sign = v > 0 ? '+' : '';
    return `${sign}${v}%`;
};

const SCOPE_OPTIONS = [
    { value: 'all', label: 'Todas as vendas', hint: 'Histórico completo do produto' },
    { value: 'from_now', label: 'A partir de agora', hint: 'Só deals ganhos depois de incluir' },
    { value: 'from_date', label: 'A partir de uma data', hint: 'Escolha a data de corte' },
] as const;

type ProductFilter = 'all' | 'sold' | 'unsold';
type PeriodTab = 0 | 1 | 2 | 3 | 4;
type SalesScope = (typeof SCOPE_OPTIONS)[number]['value'];

type ProductSearchHit = {
    id: number;
    code: string | null;
    name: string;
    line: string | null;
    supplier: string | null;
    image_url: string | null;
};

function scopeBadge(scope: GluteleaderProductSummary['sales_scope'], effectiveFrom: string | null) {
    if (!scope) return null;
    const opt = SCOPE_OPTIONS.find((o) => o.value === scope);
    const dateHint =
        scope === 'from_date' && effectiveFrom
            ? ` · ${new Date(effectiveFrom + 'T12:00:00').toLocaleDateString('pt-BR')}`
            : scope === 'from_now' && effectiveFrom
              ? ` · ${new Date(effectiveFrom + 'T12:00:00').toLocaleDateString('pt-BR')}`
              : '';
    return (
        <span className="inline-flex rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            {opt?.label ?? scope}
            {dateHint}
        </span>
    );
}

function RoyaltyHeroCard({ b, periodLabel }: { b: GluteleaderBeneficiary; periodLabel: string }) {
    const progress = b.amount_generated > 0 ? Math.min(100, (b.amount_paid / b.amount_generated) * 100) : 0;
    const isLala = b.key === 'lala';

    return (
        <div
            className={cn(
                'relative overflow-hidden rounded-2xl border-2 p-6 shadow-sm',
                isLala
                    ? 'border-orange-300 bg-gradient-to-br from-orange-100 via-white to-amber-50 dark:border-orange-800 dark:from-orange-950/40 dark:via-zinc-900 dark:to-amber-950/20'
                    : 'border-indigo-300 bg-gradient-to-br from-indigo-100 via-white to-violet-50 dark:border-indigo-800 dark:from-indigo-950/40 dark:via-zinc-900 dark:to-violet-950/20',
            )}
        >
            <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/40 blur-2xl dark:bg-white/5" />
            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start">
                {b.avatar_url ? (
                    <img
                        src={b.avatar_url}
                        alt={b.name}
                        className={cn(
                            'h-32 w-32 flex-shrink-0 rounded-2xl border-4 object-cover shadow-lg',
                            isLala ? 'border-orange-300' : 'border-indigo-300',
                        )}
                    />
                ) : (
                    <div
                        className={cn(
                            'flex h-32 w-32 flex-shrink-0 items-center justify-center rounded-2xl text-3xl font-bold',
                            isLala ? 'bg-orange-200 text-orange-800' : 'bg-indigo-200 text-indigo-800',
                        )}
                    >
                        {b.name.charAt(0)}
                    </div>
                )}

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Royalty GLUTELEADER</p>
                            <h2 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{b.name}</h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{b.email ?? '—'}</p>
                        </div>
                        <div className="text-right">
                            <span
                                className={cn(
                                    'inline-flex rounded-full px-3 py-1 text-sm font-bold text-white',
                                    isLala ? 'bg-orange-600' : 'bg-indigo-600',
                                )}
                            >
                                {b.rate_pct}% do bruto
                            </span>
                            <p className="mt-1 text-xs text-gray-500">{b.share_pct}% do pool de royalties</p>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <div className="rounded-xl bg-white/80 p-3 shadow-sm dark:bg-zinc-900/80">
                            <p className="text-[11px] font-semibold uppercase text-gray-500">Período ({periodLabel})</p>
                            <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{brl(b.amount_generated)}</p>
                            <p className="text-xs text-gray-500">sobre {brl(b.gross_base)} bruto</p>
                        </div>
                        <div className="rounded-xl bg-white/80 p-3 shadow-sm dark:bg-zinc-900/80">
                            <p className="text-[11px] font-semibold uppercase text-gray-500">Acumulado no ano</p>
                            <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{brl(b.amount_ytd)}</p>
                        </div>
                        <div className="rounded-xl bg-emerald-50 p-3 dark:bg-emerald-950/30">
                            <p className="text-[11px] font-semibold uppercase text-emerald-700">Pago</p>
                            <p className="mt-1 text-xl font-bold text-emerald-800 dark:text-emerald-300">{brl(b.amount_paid)}</p>
                        </div>
                        <div className="rounded-xl bg-amber-50 p-3 dark:bg-amber-950/30">
                            <p className="text-[11px] font-semibold uppercase text-amber-700">Pendente</p>
                            <p className="mt-1 text-xl font-bold text-amber-800 dark:text-amber-300">{brl(b.amount_pending)}</p>
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-600">
                            <span>Liquidado vs gerado no período</span>
                            <span className="font-semibold">{Math.round(progress)}%</span>
                        </div>
                        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-gray-200/80 dark:bg-zinc-700">
                            <div
                                className={cn('h-full rounded-full transition-all', isLala ? 'bg-orange-500' : 'bg-indigo-500')}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {b.quarter_closed && (
                        <span className="mt-3 inline-flex rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">
                            Trimestre fechado — provisionado
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function exportProductsCsv(products: GluteleaderProductSummary[], periodLabel: string) {
    const header = ['Código', 'Produto', 'Regra vendas', 'Qtd', 'Receita', '% total', 'Deals'];
    const rows = products.map((p) => [
        p.code ?? '',
        p.name,
        p.sales_scope_label ?? '',
        String(p.quantity_sold),
        String(p.revenue),
        String(p.share_pct),
        String(p.deals_count),
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gluteleader-produtos-${periodLabel.replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

export default function GluteleaderDashboardPage() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [quarter, setQuarter] = useState<PeriodTab>(Math.ceil((now.getMonth() + 1) / 3) as PeriodTab);
    const [productFilter, setProductFilter] = useState<ProductFilter>('all');
    const [showDeals, setShowDeals] = useState(false);
    const [manageMode, setManageMode] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const [addScope, setAddScope] = useState<SalesScope>('all');
    const [addDate, setAddDate] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ProductSearchHit | null>(null);
    const [editRow, setEditRow] = useState<GluteleaderProductSummary | null>(null);
    const [editScope, setEditScope] = useState<SalesScope>('all');
    const [editDate, setEditDate] = useState('');
    const [removeRow, setRemoveRow] = useState<GluteleaderProductSummary | null>(null);
    const qc = useQueryClient();

    const yearOptions = useMemo(() => {
        const cur = now.getFullYear();
        return [cur - 2, cur - 1, cur, cur + 1];
    }, [now]);

    const { data, isLoading, isError } = useQuery<{ data: GluteleaderDashboard }>({
        queryKey: ['commissioning', 'gluteleader', year, quarter],
        queryFn: () =>
            api
                .get('/commissioning/gluteleader-dashboard', {
                    params: quarter === 0 ? { year, scope: 'year' } : { year, quarter },
                })
                .then((r) => r.data),
    });

    const { data: searchResults = [] } = useQuery<ProductSearchHit[]>({
        queryKey: ['commissioning', 'gluteleader', 'search', searchTerm],
        enabled: manageMode && searchTerm.trim().length >= 2,
        queryFn: () =>
            api
                .get('/commissioning/gluteleader-products/search', { params: { q: searchTerm.trim() } })
                .then((r) => r.data.data),
    });

    const invalidate = () => qc.invalidateQueries({ queryKey: ['commissioning', 'gluteleader'] });

    const addProduct = useMutation({
        mutationFn: (payload: { product_id: number; sales_scope: SalesScope; effective_from?: string }) =>
            api.post('/commissioning/gluteleader-products', payload),
        onSuccess: () => {
            invalidate();
            setAddOpen(false);
            setSelectedProduct(null);
            setSearchTerm('');
            setAddScope('all');
            setAddDate('');
        },
    });

    const updateProduct = useMutation({
        mutationFn: (payload: { id: number; sales_scope: SalesScope; effective_from?: string }) =>
            api.patch(`/commissioning/gluteleader-products/${payload.id}`, {
                sales_scope: payload.sales_scope,
                effective_from: payload.effective_from,
            }),
        onSuccess: () => {
            invalidate();
            setEditRow(null);
        },
    });

    const removeProduct = useMutation({
        mutationFn: (id: number) => api.delete(`/commissioning/gluteleader-products/${id}`),
        onSuccess: () => {
            invalidate();
            setRemoveRow(null);
        },
    });

    const dash = data?.data;
    const isYearScope = quarter === 0;
    // CFO e demais leitores: só visualizam; operar fica com Bruno/Mario/super admin.
    const canManage = window.__V2__?.can_manage_gluteleader === true;

    const filteredProducts = useMemo(() => {
        if (!dash) return [];
        if (productFilter === 'sold') return dash.products_summary.filter((p) => p.has_sales);
        if (productFilter === 'unsold') return dash.products_summary.filter((p) => !p.has_sales);
        return dash.products_summary;
    }, [dash, productFilter]);

    const maxRevenue = useMemo(
        () => Math.max(...(dash?.products_summary.filter((p) => p.has_sales).map((p) => p.revenue) ?? [0]), 1),
        [dash],
    );

    useEffect(() => {
        if (editRow) {
            setEditScope((editRow.sales_scope as SalesScope) ?? 'all');
            setEditDate(editRow.effective_from ?? '');
        }
    }, [editRow]);

    const submitAdd = () => {
        if (!selectedProduct) return;
        addProduct.mutate({
            product_id: selectedProduct.id,
            sales_scope: addScope,
            effective_from: addScope === 'from_date' ? addDate : undefined,
        });
    };

    const submitEdit = () => {
        if (!editRow?.registry_id) return;
        updateProduct.mutate({
            id: editRow.registry_id,
            sales_scope: editScope,
            effective_from: editScope === 'from_date' ? editDate : undefined,
        });
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="GLUTELEADER"
                subtitle={
                    dash
                        ? `${dash.period.label} · ${dash.kpis.products_sold_count} de ${dash.kpis.catalog_count} produtos vendidos · royalty configurável`
                        : 'Painel executivo — vendas e royalty da linha GLUTELEADER'
                }
            />

            <div className="flex flex-wrap items-center gap-3">
                <select
                    className="min-w-[6.5rem] cursor-pointer appearance-none rounded-lg border border-gray-300 bg-white bg-[length:0.75rem] bg-[position:right_0.75rem_center] bg-no-repeat py-2.5 pl-3.5 pr-10 text-sm font-medium text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-gray-100"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E")`,
                    }}
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                >
                    {yearOptions.map((y) => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </select>

                <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-zinc-700 dark:bg-zinc-800">
                    {([1, 2, 3, 4, 0] as PeriodTab[]).map((q) => (
                        <button
                            key={q}
                            type="button"
                            onClick={() => setQuarter(q)}
                            className={cn(
                                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                                quarter === q
                                    ? 'bg-white text-orange-700 shadow-sm dark:bg-zinc-900 dark:text-orange-400'
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400',
                            )}
                        >
                            {q === 0 ? 'Ano' : `T${q}`}
                        </button>
                    ))}
                </div>

                {canManage && (
                <button
                    type="button"
                    onClick={() => setManageMode((v) => !v)}
                    className={cn(
                        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                        manageMode
                            ? 'border-orange-500 bg-orange-600 text-white'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-300',
                    )}
                >
                    <PiGearBold className="h-4 w-4" />
                    {manageMode ? 'Sair da edição' : 'Gerenciar catálogo'}
                </button>
                )}

                {!isYearScope && dash?.kpis.quarter_closed && (
                    <span className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">
                        Trimestre fechado
                    </span>
                )}

                {dash && dash.products_summary.length > 0 && (
                    <button
                        type="button"
                        onClick={() => exportProductsCsv(dash.products_summary, dash.period.label)}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-300"
                    >
                        <PiDownloadBold className="h-4 w-4" />
                        Exportar produtos
                    </button>
                )}
            </div>

            {isLoading && <LoadingState variant="dashboard" />}
            {isError && <p className="text-sm text-rose-600">Sem permissão ou erro ao carregar.</p>}

            {/* Pagamento dos influenciadores: ganho por parcela + saques na wallet (substitui o fechamento trimestral). */}
            <GluteleaderInfluencersSection />

            {dash && (
                <>
                    <section className="grid gap-4 xl:grid-cols-2">
                        {dash.beneficiaries.map((b) => (
                            <RoyaltyHeroCard key={b.key} b={b} periodLabel={dash.period.label} />
                        ))}
                    </section>

                    <MetricGrid>
                        <MetricCard
                            label="Bruto vendido"
                            value={brl(dash.kpis.gross_total)}
                            icon={<PiTrendUpBold className="text-orange-500" />}
                        />
                        <MetricCard
                            label="Bruto no ano"
                            value={brl(dash.kpis.gross_ytd)}
                            icon={<PiCalendarBlankBold className="text-sky-500" />}
                        />
                        <MetricCard
                            label="Ticket médio / deal"
                            value={brl(
                                dash.kpis.deals_count > 0
                                    ? dash.kpis.gross_total / dash.kpis.deals_count
                                    : 0,
                            )}
                            icon={<PiReceiptBold className="text-indigo-500" />}
                        />
                        <MetricCard
                            label="Deals com GL"
                            value={dash.kpis.deals_count}
                            icon={<PiUsersBold className="text-emerald-500" />}
                        />
                        <MetricCard
                            label="Produtos vendidos"
                            value={`${dash.kpis.products_sold_count}/${dash.kpis.catalog_count}`}
                            icon={<PiPackageBold className="text-violet-500" />}
                        />
                        <MetricCard
                            label="Unidades"
                            value={qty(dash.kpis.quantity_sold)}
                            icon={<PiShoppingCartBold className="text-blue-500" />}
                        />
                        <MetricCard
                            label="Royalty gerado"
                            value={brl(dash.kpis.commission_generated)}
                            tone="ok"
                            icon={<PiCurrencyDollarBold className="text-emerald-500" />}
                        />
                        <MetricCard
                            label="Royalty pendente"
                            value={brl(dash.kpis.commission_pending)}
                            tone={dash.kpis.commission_pending > 0 ? 'warn' : 'default'}
                            icon={<PiHourglassBold className="text-amber-500" />}
                        />
                    </MetricGrid>

                    {!isYearScope && dash.kpis.vs_prev_quarter && (
                        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-gray-400">
                            <span className="font-medium text-gray-800 dark:text-gray-200">
                                Vs T{dash.kpis.vs_prev_quarter.prev_quarter}/{dash.kpis.vs_prev_quarter.prev_year}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                Bruto:
                                {(dash.kpis.vs_prev_quarter.gross_pct ?? 0) >= 0 ? (
                                    <PiArrowUpRightBold className="h-4 w-4 text-emerald-600" />
                                ) : (
                                    <PiArrowDownRightBold className="h-4 w-4 text-rose-600" />
                                )}
                                <strong>{pctLabel(dash.kpis.vs_prev_quarter.gross_pct)}</strong>
                            </span>
                            <span className="inline-flex items-center gap-1">
                                Royalty:
                                {(dash.kpis.vs_prev_quarter.commission_pct ?? 0) >= 0 ? (
                                    <PiArrowUpRightBold className="h-4 w-4 text-emerald-600" />
                                ) : (
                                    <PiArrowDownRightBold className="h-4 w-4 text-rose-600" />
                                )}
                                <strong>{pctLabel(dash.kpis.vs_prev_quarter.commission_pct)}</strong>
                            </span>
                            <span className="text-xs text-gray-500">
                                Royalty pago no período: <strong className="text-emerald-700">{brl(dash.kpis.commission_paid)}</strong>
                            </span>
                        </div>
                    )}

                    {isYearScope && (
                        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/50">
                            <span>
                                Royalty pago no ano:{' '}
                                <strong className="text-emerald-700">{brl(dash.kpis.commission_paid)}</strong>
                            </span>
                            <span>
                                Sem venda no catálogo:{' '}
                                <strong className="text-gray-900 dark:text-white">
                                    {Math.max(0, dash.kpis.catalog_count - dash.kpis.products_sold_count)}
                                </strong>
                            </span>
                        </div>
                    )}

                    {manageMode && (
                        <div className="rounded-2xl border border-orange-200 bg-orange-50/60 p-5 dark:border-orange-900/50 dark:bg-orange-950/20">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Catálogo royalty — edição</h3>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                        Adicione ou remova produtos e defina se as vendas entram desde o histórico, a partir de hoje ou de uma data.
                                    </p>
                                </div>
                                <Button color="primary" onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1.5">
                                    <PiPlusBold className="h-4 w-4" />
                                    Adicionar produto
                                </Button>
                            </div>
                            <div className="relative mt-4">
                                <Input
                                    placeholder="Buscar produto por código ou nome (mín. 2 letras)…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchResults.length > 0 && searchTerm.length >= 2 && (
                                    <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
                                        {searchResults.map((p) => (
                                            <li key={p.id}>
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-orange-50 dark:hover:bg-orange-950/20"
                                                    onClick={() => {
                                                        setSelectedProduct(p);
                                                        setAddOpen(true);
                                                        setSearchTerm('');
                                                    }}
                                                >
                                                    {p.image_url ? (
                                                        <img src={p.image_url} alt="" className="h-12 w-12 rounded-lg border border-gray-200 object-contain bg-white p-0.5" />
                                                    ) : (
                                                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 text-xs">GL</div>
                                                    )}
                                                    <span>
                                                        <strong className="font-mono text-xs">{p.code}</strong> — {p.name}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <p className="mt-2 text-xs text-gray-500">{dash.registry.length} produtos no catálogo royalty</p>
                        </div>
                    )}

                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4 dark:border-zinc-800 dark:bg-zinc-800/50">
                            <div>
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Vendas por produto</h2>
                                <p className="text-xs text-gray-500">{filteredProducts.length} itens · regra de contagem por produto</p>
                            </div>
                            <div className="flex rounded-lg border border-gray-200 bg-white p-1 dark:border-zinc-600 dark:bg-zinc-900">
                                {([
                                    ['all', 'Todos'],
                                    ['sold', 'Com venda'],
                                    ['unsold', 'Sem venda'],
                                ] as const).map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setProductFilter(key)}
                                        className={cn(
                                            'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                                            productFilter === key ? 'bg-orange-600 text-white' : 'text-gray-600 hover:text-gray-900',
                                        )}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[980px] text-sm">
                                <thead className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/80">
                                    <tr>
                                        <th className="px-4 py-3 w-24">Produto</th>
                                        <th className="px-4 py-3">Código</th>
                                        <th className="px-4 py-3">Nome</th>
                                        <th className="px-4 py-3">Regra vendas</th>
                                        <th className="px-4 py-3 text-center">Qtd</th>
                                        <th className="px-4 py-3 text-right">Receita</th>
                                        <th className="px-4 py-3 w-36">Participação</th>
                                        <th className="px-4 py-3 text-center">Deals</th>
                                        {manageMode && <th className="px-4 py-3 w-24">Ações</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                                    {filteredProducts.map((product) => (
                                        <tr
                                            key={`${product.registry_id ?? 'o'}-${product.product_id ?? product.code}`}
                                            className={cn(
                                                'align-middle transition-colors',
                                                product.has_sales
                                                    ? 'hover:bg-orange-50/40 dark:hover:bg-orange-950/10'
                                                    : 'opacity-60',
                                            )}
                                        >
                                            <td className="px-4 py-3">
                                                {product.image_url ? (
                                                    <div className="h-20 w-20 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-black/5 dark:border-zinc-700 dark:bg-zinc-800">
                                                        <img
                                                            src={product.image_url}
                                                            alt={product.name}
                                                            className="h-full w-full object-contain p-1.5"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm font-semibold text-gray-400 dark:border-zinc-600 dark:bg-zinc-800">
                                                        GL
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {product.code ? (
                                                    <span className="inline-flex rounded-md bg-slate-800 px-2.5 py-1 font-mono text-xs font-semibold tracking-wide text-white dark:bg-slate-200 dark:text-slate-900">
                                                        {product.code}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {product.product_id ? (
                                                    <Link
                                                        to={`/products/${product.product_id}`}
                                                        className="font-semibold text-gray-900 hover:text-orange-700 dark:text-white dark:hover:text-orange-400"
                                                    >
                                                        {product.name}
                                                    </Link>
                                                ) : (
                                                    <span className="font-semibold text-gray-900 dark:text-white">{product.name}</span>
                                                )}
                                                {product.supplier && (
                                                    <p className="mt-0.5 text-xs text-gray-500">{product.supplier}</p>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {scopeBadge(product.sales_scope, product.effective_from) ?? (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {product.has_sales ? (
                                                    <span className="inline-flex min-w-[2.75rem] items-center justify-center rounded-full bg-orange-600 px-2.5 py-1 text-xs font-bold tabular-nums text-white">
                                                        {qty(product.quantity_sold)}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex min-w-[2.75rem] items-center justify-center rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold tabular-nums text-gray-600 dark:bg-zinc-700 dark:text-gray-300">
                                                        0
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {product.has_sales ? (
                                                    <span className="inline-flex rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-bold tabular-nums text-white">
                                                        {brl(product.revenue)}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {product.has_sales ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
                                                            <div
                                                                className="h-full rounded-full bg-orange-500"
                                                                style={{ width: `${Math.max(4, (product.revenue / maxRevenue) * 100)}%` }}
                                                            />
                                                        </div>
                                                        <span className="w-10 text-right text-xs tabular-nums text-gray-500">
                                                            {product.share_pct}%
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400">0%</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {product.deals_count > 0 ? (
                                                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-full bg-blue-600 px-2.5 py-1 text-xs font-bold tabular-nums text-white">
                                                        {product.deals_count}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                            {manageMode && (
                                                <td className="px-4 py-3">
                                                    {product.registry_id ? (
                                                        <div className="flex gap-1">
                                                            <button
                                                                type="button"
                                                                title="Editar regra"
                                                                onClick={() => setEditRow(product)}
                                                                className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-orange-700"
                                                            >
                                                                <PiPencilSimpleBold className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                title="Remover do catálogo"
                                                                onClick={() => setRemoveRow(product)}
                                                                className="rounded p-1.5 text-gray-500 hover:bg-rose-50 hover:text-rose-600"
                                                            >
                                                                <PiTrashBold className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    ) : null}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="grid gap-5 lg:grid-cols-3">
                        <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-700 dark:bg-zinc-900 lg:col-span-2">
                            <div className="mb-4 flex items-center gap-2">
                                <PiChartBarBold className="h-5 w-5 text-orange-600" />
                                <h2 className="font-semibold text-gray-900 dark:text-white">Evolução mês a mês</h2>
                            </div>
                            {dash.by_month.labels.length > 0 ? (
                                <MonthlyComboChart
                                    labels={dash.by_month.labels}
                                    paid={dash.by_month.gross}
                                    accrued={dash.by_month.commission_generated}
                                    cumulative={dash.by_month.cumulative_gross}
                                />
                            ) : (
                                <p className="py-12 text-center text-sm text-gray-400">Sem vendas no período.</p>
                            )}
                        </div>
                        <div className="space-y-4">
                            <div className="rounded-xl border border-gray-200 p-4 dark:border-zinc-700">
                                <h3 className="mb-2 text-sm font-semibold">Top vendedores</h3>
                                <ul className="space-y-2">
                                    {dash.top_sellers.map((s, i) => (
                                        <li key={s.label} className="flex justify-between text-sm">
                                            <span>{i + 1}. {s.label}</span>
                                            <span className="font-semibold">{brl(s.total)}</span>
                                        </li>
                                    ))}
                                    {dash.top_sellers.length === 0 && <p className="text-xs text-gray-400">Nenhum no período.</p>}
                                </ul>
                            </div>
                            <div className="rounded-xl border border-gray-200 p-4 dark:border-zinc-700">
                                <h3 className="mb-2 text-sm font-semibold">Trimestres {dash.period.year}</h3>
                                {dash.by_quarter.map((q) => (
                                    <div key={q.quarter} className="flex justify-between py-1.5 text-sm">
                                        <span>{q.label}</span>
                                        <span className="font-semibold">{brl(q.gross)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {dash.payouts.length > 0 && (
                        <div className="rounded-xl border border-gray-200 p-4 dark:border-zinc-700">
                            <h3 className="mb-3 text-sm font-semibold">Histórico de fechamentos</h3>
                            <table className="w-full text-sm">
                                <tbody className="divide-y divide-gray-50">
                                    {dash.payouts.map((p) => (
                                        <tr key={p.record_id}>
                                            <td className="py-2">{p.period}</td>
                                            <td className="py-2">{p.beneficiary_name}</td>
                                            <td className="py-2 text-right font-medium">{brl(p.amount)}</td>
                                            <td className="py-2 capitalize">{p.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-zinc-700">
                        <button
                            type="button"
                            onClick={() => setShowDeals((v) => !v)}
                            className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 dark:bg-zinc-800/50"
                        >
                            <span className="text-sm font-semibold">Detalhe por deal ({dash.kpis.lines_count} linhas)</span>
                            {showDeals ? <PiCaretDownBold /> : <PiCaretRightBold />}
                        </button>
                        {showDeals && (
                            <table className="w-full text-sm">
                                <tbody className="divide-y divide-gray-50">
                                    {dash.lines.map((line) => (
                                        <tr key={`${line.deal_id}-${line.product_code}`}>
                                            <td className="px-4 py-2">
                                                <Link to={`/ploomes-deals/${line.deal_id}`} className="text-orange-700 hover:underline">
                                                    {line.deal_number ?? line.deal_id}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-2">{line.product_name}</td>
                                            <td className="px-4 py-2">{line.owner_name}</td>
                                            <td className="px-4 py-2 text-right font-semibold">{brl(line.line_total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}

            <ConfirmModal
                open={addOpen}
                onClose={() => {
                    setAddOpen(false);
                    setSelectedProduct(null);
                }}
                onConfirm={submitAdd}
                title="Incluir produto no royalty"
                description={selectedProduct ? `${selectedProduct.code} — ${selectedProduct.name}` : 'Busque e selecione um produto.'}
                confirmLabel="Incluir"
                loading={addProduct.isPending}
            >
                <div className="space-y-3">
                    {SCOPE_OPTIONS.map((opt) => (
                        <label key={opt.value} className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-gray-50 dark:hover:bg-zinc-800">
                            <input
                                type="radio"
                                name="add_scope"
                                checked={addScope === opt.value}
                                onChange={() => setAddScope(opt.value)}
                                className="mt-1"
                            />
                            <span>
                                <span className="font-medium">{opt.label}</span>
                                <span className="block text-xs text-gray-500">{opt.hint}</span>
                            </span>
                        </label>
                    ))}
                    {addScope === 'from_date' && (
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Data de corte</label>
                            <Input type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} />
                        </div>
                    )}
                </div>
            </ConfirmModal>

            <ConfirmModal
                open={!!editRow}
                onClose={() => setEditRow(null)}
                onConfirm={submitEdit}
                title="Editar regra de vendas"
                description={editRow ? `${editRow.code} — ${editRow.name}` : undefined}
                confirmLabel="Salvar"
                loading={updateProduct.isPending}
            >
                <div className="space-y-3">
                    {SCOPE_OPTIONS.map((opt) => (
                        <label key={opt.value} className="flex cursor-pointer items-start gap-3 rounded-lg border p-3">
                            <input
                                type="radio"
                                name="edit_scope"
                                checked={editScope === opt.value}
                                onChange={() => setEditScope(opt.value)}
                                className="mt-1"
                            />
                            <span>
                                <span className="font-medium">{opt.label}</span>
                                <span className="block text-xs text-gray-500">{opt.hint}</span>
                            </span>
                        </label>
                    ))}
                    {editScope === 'from_date' && (
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Data de corte</label>
                            <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                        </div>
                    )}
                </div>
            </ConfirmModal>

            <ConfirmModal
                open={!!removeRow}
                onClose={() => setRemoveRow(null)}
                onConfirm={() => removeRow?.registry_id && removeProduct.mutate(removeRow.registry_id)}
                title="Remover do catálogo royalty?"
                description={removeRow ? `${removeRow.name} deixará de contar nas vendas GLUTELEADER.` : undefined}
                confirmLabel="Remover"
                loading={removeProduct.isPending}
            />
        </div>
    );
}
